import { create } from '@bufbuild/protobuf'
import {
  ApprovalMode,
  BusinessProcess,
  PlanningSection,
  WorkflowSchedule,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import {
  ApprovalFlowCellSchema,
  type ApprovalFlowProcessSetting,
  ApprovalFlowStageSchema,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'

import type { StageLocal } from '~/components/approval/stage'
import {
  type CellKey,
  PROCESS_DEFS,
  type ProcessDef,
  makeCellKey,
  makeNoSectionCellKey,
  parseCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import {
  type CellLocal,
  type ProcessState,
} from '~/pages/AdminApprovalFlow/models/reducer'

/**
 * プロセスの全セルを「承認なし (NO_APPROVAL, 段階未設定)」で初期化したマップを返す。
 *
 * spec §1「「一括」セレクトの初期値」(2026-08-04 PdM 判断、issue #4250 論点1、C 案):
 * 新規テナント / 新規導入時の既定は「承認なし」(担当者手動完了で業務進行可能、鉄則2
 * 抵触なし。承認段階を必須にすると初期投入コストが高いため)。
 * backend がエリアのセルを返さない場合でも、 この NO_APPROVAL 既定で全セルを
 * 埋めることで表示を spec §1 のデフォルトに一致させる
 * (= emptyProcessState / convertProcessSetting で共通利用)。
 *
 * 補足 (Cross-slice Invariant): この定義層 default (NO_APPROVAL) の反転は、消費側
 * resolver (`ResolveCompletionOutcome` / `isSubStepComplete`) の fail-closed guard
 * (APPROVAL セル + ゲートなし → 承認待) には影響しない (guard は APPROVAL セルにのみ
 * 発火。cell の出自によらず維持)。明示保存された NO_APPROVAL セルは Complete で通過する。
 * なお同 resolver は「セル行なし (mode = None)」も Complete で通す非対称があり、
 * 表示側既定 (NO_APPROVAL) との一貫はあるが承認ゲートを迂回する既知 gap として別課題で追跡。
 */
function buildDefaultCells(procDef: ProcessDef): Map<CellKey, CellLocal> {
  const cells = new Map<CellKey, CellLocal>()
  for (const area of procDef.areas) {
    const key = makeNoSectionCellKey(procDef.businessProcess, area)
    cells.set(key, { mode: ApprovalMode.NO_APPROVAL, stages: [] })
  }
  return cells
}

/**
 * 設定が存在しないプロセスの初期 ProcessState を生成する。
 *
 * spec §1「「一括」セレクトの初期値」: 新規テナント default = 承認なし。
 * 全セルを ApprovalMode.NO_APPROVAL (段階未設定) で初期化する (buildDefaultCells)。
 *
 * backend の ListProcesses は設定済みプロセス行のみ返すため、
 * 新規組織 (DB に承認フロー設定が 1 行も無い) では空配列が返る。
 * PROCESS_DEFS の入札 2 業務処理を SoT として描画するために、
 * 設定が無いプロセスはこの helper で全セル承認なし (段階未設定) の ProcessState を生成する。
 */
export function emptyProcessState(
  businessProcess: BusinessProcess,
): ProcessState {
  const procDef = PROCESS_DEFS.find(
    (p) => p.businessProcess === businessProcess,
  )
  const cells =
    procDef !== undefined
      ? buildDefaultCells(procDef)
      : new Map<CellKey, CellLocal>()

  return {
    businessProcess,
    cells,
    // optimistic lock 用に proto Timestamp で保持。未設定時は undefined
    updatedAt: undefined,
    dirty: false,
    // 初期状態を RESET_PROCESS 用スナップショットとして保持
    snapshot: { cells, updatedAt: undefined },
  }
}

/**
 * proto ApprovalFlowProcessSetting → ProcessState
 *
 * セルは「全セル承認なし (NO_APPROVAL, 段階未設定)」を土台にし、 backend が返したセルだけで
 * 上書きする。 backend が返さない断面・エリア、 および UNSPECIFIED で返ってきたセルは
 * NO_APPROVAL 既定のまま残す (spec §1 の初期値に準拠。 Cross-slice Invariant は
 * buildDefaultCells の godoc 参照 — 定義層 default の反転は消費側 fail-closed
 * guard に影響せず、消費側は「セル行なし」経路を Complete で通す既知 gap を持つ)。
 * 「プロセス行は返るが cells が空 (新規組織直後など)」のレスポンスでも、
 * 承認なし既定のため承認段階の設定なしに保存できる (設計書 AC-01)。
 */
export function convertProcessSetting(
  setting: ApprovalFlowProcessSetting,
): ProcessState {
  const procDef = PROCESS_DEFS.find(
    (p) => p.businessProcess === setting.businessProcess,
  )
  const cells =
    procDef !== undefined
      ? buildDefaultCells(procDef)
      : new Map<CellKey, CellLocal>()

  // 画面が扱うセルの CellKey 以外 (断面付きの旧データ等) を除外し、save で送り返されるのを防ぐ。
  const validKeys = new Set(cells.keys())

  for (const cell of setting.cells) {
    // UNSPECIFIED (= backend 側でも未設定) は NO_APPROVAL (承認なし・段階未設定) 既定を
    // 維持し上書きしない (spec §1 の初期値に準拠)
    if (cell.approvalMode === ApprovalMode.UNSPECIFIED) continue
    const key = makeCellKey(
      setting.businessProcess,
      cell.section ?? PlanningSection.UNSPECIFIED,
      cell.schedule ?? WorkflowSchedule.UNSPECIFIED,
      cell.areaCode,
    )
    if (!validKeys.has(key)) continue
    const stages: StageLocal[] = cell.stages.map((s) => ({
      strategy: s.strategy,
      approverMemberIds: [...s.approverMemberIds],
    }))
    cells.set(key, { mode: cell.approvalMode, stages })
  }

  return {
    businessProcess: setting.businessProcess,
    cells,
    // optimistic lock 用に proto Timestamp のまま保持 (表示時のみ timestampDate 変換)
    updatedAt: setting.updatedAt,
    dirty: false,
    // サーバー取得時点を RESET_PROCESS 用スナップショットとして保持
    snapshot: { cells, updatedAt: setting.updatedAt },
  }
}

/**
 * ProcessState → SaveApprovalFlowRequest に渡す cells
 * 未設定 (mode=UNSPECIFIED) のセルは送信しない (proto spec 準拠)。
 * 入札 2 業務処理のセルは断面・スケジュールを持たないので送らない。
 */
export function convertProcessStateToRequest(
  state: ProcessState,
): Array<ReturnType<typeof create<typeof ApprovalFlowCellSchema>>> {
  return Array.from(state.cells.entries())
    .filter(([, cell]) => cell.mode !== ApprovalMode.UNSPECIFIED)
    .map(([key, cell]) => {
      const stages = cell.stages.map((s, i) =>
        create(ApprovalFlowStageSchema, {
          stageOrder: i + 1,
          strategy: s.strategy,
          approverMemberIds: [...s.approverMemberIds],
        }),
      )
      return create(ApprovalFlowCellSchema, {
        areaCode: parseCellKey(key).areaCode,
        approvalMode: cell.mode,
        stages,
      })
    })
}
